import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';

import { ItemTabComponent } from '../../_basic/tab-playlist-item.component';
import { PlaylistItemType } from '../../../core/models/enums/playlist-item-type.enum';
import { Launcher } from '../../../core/models/entities/launcher.type';
import { Machine } from '../../../core/models/entities/machine.type';
import { ScreensStateService } from '../../../core/services/screen-state/screens-state.service';
import { MonitorHub } from '../../../shared-module/hubs/monitor.hub';
import { MachinesService } from '../../../core/services/http-services/machines.service';
import { PlaylistItemDTO } from '../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { AppConstants } from '../../../app.constants';
import { RssTickerConfigs } from '../../../core/models/entities/rss-ticker.type';
import { PlaylistItemLinkModel } from '../../../core/models/view-models/playlist-item-models/playlist-item-link.model';
import { SortOrder, SortOrderLabels } from '../../../core/models/entities/sort-order.type';

@Component({
  selector: 'tab-local-file',
  templateUrl: './tab-local-file.component.html',
  styleUrls: [
    './tab-local-file.component.scss',
    '../../modal-playlist-item/playlist-item-dialog.component.scss'
  ]
})
export class TabLocalAppComponent extends ItemTabComponent implements OnDestroy, OnInit {
  public locations: Machine[] = [];
  public selectedlocation: string = null;
  public iconsPath = AppConstants.LAUNCHERS_ICONS_PATH;
  public rssInputPlaceholder = AppConstants.RSS_INPUT_PLACEHOLDER;
  public customParametersSubject = new Subject<PlaylistItemLinkModel>;
  public sortOrderEntries = Object.entries(SortOrderLabels).map(([value, label]) => ({ value: Number(value), label }));
  private isChangedLocation: boolean = false;

  constructor(
    private readonly screenStateService: ScreensStateService,
    private readonly monHub: MonitorHub,
    private readonly machineService: MachinesService,
    private readonly destroyRef: DestroyRef
  ) {
    super();
    this.subscriptions.add(
      this.launchersService.localLaunchersSubject
        .pipe(delay(300))
        .subscribe((localLaunchers: Launcher[]) => {
          this.launchers = [...(this.launchers ? this.launchers : []), ...(localLaunchers ? localLaunchers : [])];
          if (this.launchers && this.launchers.length && this.playlistItem) {
            super.setSelectedLauncher(this.playlistItem);
          }
        })
    );
  }

  ngOnInit() {
    this.subscriptions.add(
      this.screenStateService.locationsChanged.subscribe((data: Machine[]) => {
        const availableLocations = data.filter(
          location => this.playlist.IsAndroid ? location.IsAndroid : !location.IsAndroid
        );

        this.locations = [...(availableLocations ? availableLocations : [])];
      })
    );

    if (!this.playlistItem.IsLocal) {
      this.playlistItem = new PlaylistItemDTO();
      this.playlistItem.IsLocal = true;
      this.playlistItem.PlaylistItemLinks.push(new PlaylistItemLinkModel());
      this.selectedLauncher = null;

      if (this.playlist.IsAndroid) {
        this.playlistItem.Order = SortOrder.Date;
        this.playlistItem.FolderItemDuration = AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION;
      }
    }

    this.playlistItem.RssConfigs = this.playlistItem.RssConfigs || new RssTickerConfigs();
    this.playlistItemName = this.playlistItem.PlaylistItemTitle;

    if (this.playlist.IsAndroid) {
      this.playlistItem.PdfPageDuration = this.playlistItem.PdfPageDuration ?? AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION;
    }

    const isLocalPlaylist: boolean = _.some(this.playlist.PlaylistItems, (playlistItem: PlaylistItemDTO) =>
      playlistItem && playlistItem.IsLocal);
    if (!isLocalPlaylist) {
      this.playlist.MachinePhysicalAddress = null;
    }

    if (this.playlist && this.playlist.MachinePhysicalAddress) {
      this.fetchLocalData(this.playlist.MachinePhysicalAddress);
      this.subscriptions.add(
        this.machineService.getLocationNameByPhysicalAddress(this.playlist.MachinePhysicalAddress)
          .subscribe((location: string) => this.selectedlocation = location)
      );
    }

    this.customParametersSubject.pipe(
      debounceTime(300),
      switchMap(source => {
        if (source.Link) {
          return this.filesService.isDirectory(source.Link)
            .pipe(
              tap(isValidPath => {
                source.Duration = isValidPath ?
                source.Duration || AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION :
                  null;
              })
            );
        }
        else {
          source.Duration = null;
          return of();
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe();
  }

  public submit(): void {
    this.playlistItem.IsLocal = true;
    this.playlistItem.PlaylistItemType = PlaylistItemType.LocalFile;

    if (this.playlistItemName) {
      this.playlistItem.PlaylistItemTitle = this.playlistItemName;
    }
    else if (this.playlistItem.PlaylistItemLinks.length > 1) {
      this.playlistItem.PlaylistItemTitle = AppConstants.MULTIPLE_ITEMS_TITLE;
    }
    else {
      this.playlistItem.PlaylistItemTitle = this.selectedLauncher?.LauncherTitle || AppConstants.LOCAL_ITEM_TITLE;
    }

    if (this.isChangedLocation) {
      this.playlist.MachinePhysicalAddress = this.selectedlocation;
      this.screenStateService.locationChanged.next(this.playlist.MachinePhysicalAddress);
      this.playlist.Machine = this.locations.find((machine: Machine) => machine.PhysicalAddress === this.playlist.MachinePhysicalAddress);
    }

    super.saveChanges([this.playlistItem]);
  }

  public onLauncherChange(event: Launcher): void {
    super.onLauncherChange(event, true, this.playlistItem);
    if (!this.playlistItemName) {
      this.playlistItem.PlaylistItemTitle = event.LauncherTitle;
    }

    if (this.selectedLauncher.ExecutableName?.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)) {
      this.playlistItem.Order = SortOrder.Date;
      this.playlistItem.IsContentStretched = true;
    }
    else {
      this.playlistItem.IsContentStretched = false;
      this.playlistItem.UseExactFolderDuration = false;
    }
  }

  public onLocationChanged(locationPhysicalAddress: string): void {
    this.isChangedLocation = true;
    if (locationPhysicalAddress) {
      this.fetchLocalData(locationPhysicalAddress);
    }
    this.selectedlocation = locationPhysicalAddress;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  isSubmitButtonDisabled() {
    return this.playlist.IsAndroid ?
      !this.playlistItem.CustomParameters :
      !this.selectedlocation || !(this.playlistItem.LocalLauncher || this.playlistItem.LauncherId);
  }

  private fetchLocalData(machinePhysicalAddress: string): void {
    this.monHub.triggerFetchingLocalData(machinePhysicalAddress);
  }
}
